import React, { useId } from 'react';

export interface ResponsiveChoiceOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  group?: string;
  hideOnMobile?: boolean;
}

export type ResponsiveChoiceMobilePresentation = 'direct' | 'select';

export interface ResponsiveChoiceProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children' | 'onChange' | 'value'> {
  value: string;
  onValueChange: (value: string) => void;
  options: readonly ResponsiveChoiceOption[];
  mobileLabel: string;
  mobileLayout?: 'chips' | 'bars';
  /** Direct choices are the default; filter sheets may retain a compact native select. */
  mobilePresentation?: ResponsiveChoiceMobilePresentation;
  wrapperClassName?: string;
  selectClassName?: string;
  selectStyle?: React.CSSProperties;
}

/**
 * ResponsiveChoice keeps the compact native select on larger screens and
 * exposes the same controlled value as direct, keyboard-operable choices on
 * mobile by default. Set mobilePresentation="select" for compact filter-sheet
 * controls where a native select remains the clearer mobile presentation.
 */
export function ResponsiveChoice({
  value,
  onValueChange,
  options,
  mobileLabel,
  mobileLayout = 'chips',
  mobilePresentation = 'direct',
  wrapperClassName,
  selectClassName,
  selectStyle,
  disabled,
  id,
  ...selectProps
}: ResponsiveChoiceProps) {
  const generatedName = useId();
  const optionIdPrefix = useId().replace(/:/g, '');
  const groups = Array.from(new Set(options.map((option) => option.group ?? '')));
  const mobileGroups = groups.filter((group) => (
    options.some((option) => (option.group ?? '') === group && !option.hideOnMobile)
  ));

  return (
    <div
      className={['ui-responsive-choice', wrapperClassName ?? ''].filter(Boolean).join(' ')}
      data-layout={mobileLayout}
      data-mobile-presentation={mobilePresentation}
      data-disabled={disabled ? 'true' : undefined}
    >
      <select
        {...selectProps}
        id={id}
        className={selectClassName}
        style={selectStyle}
        value={value}
        disabled={disabled}
        onChange={(event) => onValueChange(event.target.value)}
      >
        {groups.map((group) => {
          const groupOptions = options.filter((option) => (option.group ?? '') === group);
          const renderedOptions = groupOptions.map((option) => (
            <option key={`${group}:${option.value}`} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ));
          return group
            ? <optgroup key={group} label={group}>{renderedOptions}</optgroup>
            : <React.Fragment key="ungrouped">{renderedOptions}</React.Fragment>;
        })}
      </select>

      {mobilePresentation === 'direct' ? (
        <fieldset
          className="ui-responsive-choice__mobile"
          aria-describedby={selectProps['aria-describedby']}
          aria-invalid={selectProps['aria-invalid']}
          disabled={disabled}
        >
          <legend className="ui-responsive-choice__legend">{mobileLabel}</legend>
          {mobileGroups.map((group) => (
            <div className="ui-responsive-choice__group" key={group || 'ungrouped'}>
              {group && <span className="ui-responsive-choice__group-label">{group}</span>}
              <div className="ui-responsive-choice__options">
                {options
                  .filter((option) => (option.group ?? '') === group && !option.hideOnMobile)
                  .map((option, index) => {
                    const optionId = `${optionIdPrefix}-${mobileGroups.indexOf(group)}-${index}`;
                    const optionLabelId = `${optionId}-label`;
                    const optionDescriptionId = option.description ? `${optionId}-description` : undefined;
                    const describedBy = [optionDescriptionId, selectProps['aria-describedby']].filter(Boolean).join(' ') || undefined;
                    return (
                      <label className="ui-responsive-choice__option" htmlFor={optionId} key={`${group}:${option.value}`}>
                        <input
                          id={optionId}
                          type="radio"
                          name={generatedName}
                          value={option.value}
                          checked={value === option.value}
                          disabled={disabled || option.disabled}
                          aria-labelledby={optionLabelId}
                          aria-describedby={describedBy}
                          onChange={() => onValueChange(option.value)}
                        />
                        <span className="ui-responsive-choice__surface">
                          <span className="ui-responsive-choice__copy">
                            <strong id={optionLabelId}>{option.label}</strong>
                            {option.description && <small id={optionDescriptionId}>{option.description}</small>}
                          </span>
                          <span className="ui-responsive-choice__check" aria-hidden="true">check</span>
                        </span>
                      </label>
                    );
                  })}
              </div>
            </div>
          ))}
        </fieldset>
      ) : null}
    </div>
  );
}
